import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, PackageCheck } from "lucide-react";
import { Reveal } from "./Reveal";
import Seo from "./Seo";
import { FORMULES } from "./Packages";

const INCLUSIONS = [
  {
    title: "Transport & déplacement",
    text: "Le déplacement est inclus dans nos zones d'intervention (Drôme, Vaucluse, Ardèche). Au-delà, il est chiffré dans votre devis — jamais de surprise après signature.",
  },
  {
    title: "Montage & démontage",
    text: "Nous arrivons en avance pour installer la régie, les éclairages et les structures, et nous reprenons tout en fin de soirée. Vous n'avez rien à porter, ni à prévoir.",
  },
  {
    title: "Technicien DJ toute la soirée",
    text: "Un professionnel présent du premier invité jusqu'à 4 H du matin : lecture de la piste, transitions, micro tendu au bon moment pour chaque discours.",
  },
  {
    title: "Préparation & playlist collaborative",
    text: "Rendez-vous de préparation, playlist partagée avec vos invités via un code d'accès, titres clôturés 48 h avant, coordination avec vos témoins et prestataires.",
  },
];

export default function TarifsPage() {
  return (
    <div className="min-h-screen bg-[#080C14]" data-testid="tarifs-page">
      <Seo
        title="Tarifs & Formules | DJ mariage dès 800 € — KLMProd"
        description="Nos formules mariage de 800 € à 1 600 € : ce que comprend réellement chaque tarif (transport, montage, technicien DJ jusqu'à 4 H du matin, playlist collaborative). Devis gratuit sous 48 h."
        path="/tarifs"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "PriceSpecification",
          priceCurrency: "EUR",
          minPrice: 800,
          maxPrice: 1600,
        }}
      />

      <header className="sticky top-0 z-40 border-b border-cyan-500/15 bg-[#080C14]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="tarifs-back-link"
            className="group flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"
          >
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Retour au site
          </Link>
          <img src="/logo-v2.png" alt="KLMProd" className="h-8 w-auto" />
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-4 pt-20 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono-brand text-xs uppercase tracking-[0.3em] text-cyan-400">Tarifs & formules</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-display mt-5 max-w-3xl text-4xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
            Des prix affichés, <span className="text-gradient">assumés.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Pas de « sur demande » : nos formules mariage sont affichées, détaillées et contractuelles.
            Voici exactement ce que comprend chaque tarif.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8" data-testid="tarifs-cards">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {FORMULES.map((f, i) => (
            <Reveal key={f.name} delay={i * 0.1} className="h-full">
              <article
                className={`flex h-full flex-col rounded-3xl border p-7 ${
                  f.popular
                    ? "border-cyan-400/50 bg-[#0E1524] shadow-[0_0_60px_rgba(0,150,255,0.15)]"
                    : "border-cyan-500/15 bg-[#0E1524]/70"
                }`}
                data-testid={`tarifs-card-${f.name.toLowerCase()}`}
              >
                {f.popular && (
                  <span className="bg-brand-gradient mb-4 w-fit rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#04121f]">
                    La plus choisie
                  </span>
                )}
                <h2 className="font-display text-2xl font-bold text-slate-50">{f.name}</h2>
                <p className="mt-3 flex items-baseline gap-1.5">
                  <span className="text-sm text-slate-400">à partir de</span>
                  <span className="font-display text-gradient text-4xl font-black">
                    {f.price.toLocaleString("fr-FR")} €
                  </span>
                </p>
                <p className="mt-3 text-sm text-slate-400">{f.description}</p>
                <ul className="mt-6 flex-1 space-y-3">
                  {f.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check size={16} className="mt-0.5 shrink-0 text-cyan-400" />
                      {feat}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 text-center text-sm text-slate-500">
            Soirées d'entreprise, anniversaires, séminaires : devis dédié sous 48 h.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8" data-testid="tarifs-inclusions">
        <Reveal>
          <h2 className="font-display max-w-2xl text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl">
            Ce que comprend <span className="text-gradient">réellement</span> chaque tarif
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
          {INCLUSIONS.map((inc, i) => (
            <Reveal key={inc.title} delay={i * 0.08}>
              <div className="flex items-start gap-4 rounded-3xl border border-cyan-500/15 bg-[#0E1524]/70 p-6" data-testid={`inclusion-${i + 1}`}>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-cyan-400/25 bg-cyan-400/10">
                  <PackageCheck size={20} className="text-cyan-300" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-100">{inc.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{inc.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-3xl text-sm leading-relaxed text-slate-500">
            Options disponibles sur devis : étincelles froides, fumée lourde, éclairage architectural
            supplémentaire, sonorisation de cérémonie. Acompte de 30 % à la signature (payable en
            ligne), solde réglable en ligne avant l'événement. Nous recommandons la souscription
            d'une assurance annulation et intempéries.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <Reveal>
          <div className="rounded-[2rem] border border-cyan-400/20 bg-gradient-to-br from-[#0E1524] via-[#0B1E33] to-[#0E1524] p-10 text-center sm:p-14">
            <h2 className="font-display text-2xl font-extrabold text-slate-50 sm:text-3xl">
              Votre devis détaillé <span className="text-gradient">sous 48 h.</span>
            </h2>
            <Link
              to="/#devis"
              data-testid="tarifs-cta-devis"
              className="bg-brand-gradient group mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold text-[#04121f] transition-transform duration-300 hover:scale-105"
            >
              Demander mon devis
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
